import React from 'react';
import { Modal, Button, Tag, Spin, Typography, Divider, List, Card, Empty } from 'antd';

const { Title, Text } = Typography;

// Interface para um questionário
interface Questionario {
  questionarioId?: string;
  _id?: string;
  razaoSocial: string;
  cnpj: string;
  email: string;
  area: string;
  ultimoQuestionario: string | { status: string }; // Suporta ambos os formatos
  dataEnvio?: string;
  respostasCount?: number;
  titulo?: string;
  status?: string;
}

// Interface para uma resposta do questionário
interface Resposta {
  pergunta: string;
  resposta?: string;
  observacao?: string;
}

// Interface para as props do componente
interface QuestionarioModalProps {
  visible: boolean;
  onClose: () => void;
  questionario: Questionario | null;
  respostas: Resposta[];
  carregando: boolean;
}

const QuestionarioModal: React.FC<QuestionarioModalProps> = ({
  visible,
  onClose,
  questionario,
  respostas,
  carregando
}) => {
  // Função para determinar a cor da tag de status
  const getStatusColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'pendente') return 'gold';
    if (statusLower === 'enviado') return 'green';
    return 'blue';
  };

  // Função para determinar a cor do indicador baseado no status
  const getIndicatorColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'pendente') return '#FFC107';
    if (statusLower === 'enviado') return '#52C41A';
    return '#1890FF';
  };

  // Função para formatar o status (primeira letra maiúscula)
  const formatStatus = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Obter status do questionário
  const getStatus = (): string => {
    if (!questionario) return 'Pendente';
    
    if (typeof questionario.ultimoQuestionario === 'string') {
      return questionario.ultimoQuestionario;
    }
    
    return questionario.ultimoQuestionario?.status || 
           questionario.status || 
           'Pendente';
  };

  // Renderiza o conteúdo da modal
    const renderConteudoModal = () => {
        if (!questionario) return null;
        
        const status = getStatus();
    
        return (
        <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Title level={4}>Questionário de Fornecedor</Title>
            <Text strong>Fornecedor: </Text>
            <Text>{questionario.razaoSocial || questionario.titulo}</Text>
            <br />
            <Text strong>CNPJ: </Text>
            <Text>{questionario.cnpj}</Text>
            <br />
            <Text strong>Status: </Text>
            <Tag color={getStatusColor(status)}>
                <span style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                backgroundColor: getIndicatorColor(status),
                borderRadius: '50%',
                marginRight: 6
                }} />
                {formatStatus(status)}
            </Tag>
            <br />
            <Text strong>Data de Envio: </Text>
            <Text>{questionario.dataEnvio || 'Não enviado'}</Text>
            </div>
    
            <Divider orientation="left">Perguntas e Respostas</Divider>
    
            {carregando ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin tip="Carregando respostas..." />
            </div>
            ) : respostas.length > 0 ? (
            <List
                itemLayout="vertical"
                dataSource={respostas}
                renderItem={(item, index) => (
                <Card 
                    style={{ marginBottom: 16 }} 
                    title={`Pergunta ${index + 1}`}
                >
                    <div>
                    <Text strong>Pergunta: </Text>
                    <Text>{item.pergunta}</Text>
                    </div>
                    <div style={{ marginTop: 10 }}>
                    <Text strong>Resposta: </Text>
                    <Text>{item.resposta || 'Sem resposta'}</Text>
                    </div>
                    {item.observacao && (
                    <div style={{ marginTop: 10 }}>
                        <Text strong>Observação: </Text>
                        <Text>{item.observacao}</Text>
                    </div>
                    )}
                </Card>
                )}
            />
            ) : (
            <Empty description="Nenhuma resposta encontrada para este questionário" />
            )}
        </div>
        );
    };

  return (
    <Modal
      title="Visualização de Questionário"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="fechar" onClick={onClose}>
          Fechar
        </Button>
      ]}
      width={800}
      style={{ top: 20 }}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {renderConteudoModal()}
    </Modal>
  );
};

export default QuestionarioModal;